import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import { EnvironmentConfig, CloudConfig, BirdConfig, LightningConfig, SunConfig } from './types';
import { CloudSystem } from './clouds/CloudSystem';
import { BirdFlockSystem } from './birds/BirdFlockSystem';
import { LightningSystem } from './lightning/LightningSystem';
import { SunSystem } from './sun/SunSystem';

export class EnvironmentRoot extends SceneNode {
    public clouds?: CloudSystem;
    public birds?: BirdFlockSystem;
    public lightning?: LightningSystem;
    public sun?: SunSystem;

    constructor(cfg?: EnvironmentConfig) {
        super('EnvironmentRoot');
        // Provide a root group so SceneNodeManager can attach us to the THREE.Scene
        const group = new THREE.Group();
        group.name = 'EnvironmentRoot';
        this._mesh = group;
        this._mesh.userData.sceneNode = this;
        if (cfg?.clouds) this.enableClouds(cfg.clouds as CloudConfig);
        if (cfg?.birds) this.enableBirds(cfg.birds as BirdConfig);
        if (cfg?.lightning) this.enableLightning(cfg.lightning as LightningConfig);
        if (cfg?.sun) this.enableSun(cfg.sun as SunConfig);
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        if (this._mesh) box.setFromObject(this._mesh);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(0,0,0), new THREE.Vector3(1,1,1));
        return box;
    }

    public setScaleUniform(scale: number): void {
        if (this._mesh) {
            (this._mesh as THREE.Object3D).scale.set(scale, scale, scale);
        }
    }

    public setPositionY(y: number): void {
        if (this._mesh) {
            (this._mesh as THREE.Object3D).position.y = y;
        }
    }

    enableClouds(cfg?: Partial<CloudConfig>): void {
        if (!this.clouds) {
            this.clouds = new CloudSystem('CloudSystem', cfg);
            this.addChild(this.clouds);
            this.clouds.initialize();
        } else if (cfg) {
            this.clouds.setConfig(cfg);
        }
    }

    enableBirds(cfg?: Partial<BirdConfig>): void {
        if (!this.birds) {
            this.birds = new BirdFlockSystem('BirdFlockSystem', cfg);
            this.addChild(this.birds);
            this.birds.initialize();
        } else if (cfg) {
            this.birds.setConfig(cfg);
        }
    }

    enableLightning(cfg?: Partial<LightningConfig>): void {
        if (!this.lightning) {
            this.lightning = new LightningSystem('LightningSystem', cfg);
            this.addChild(this.lightning);
            this.lightning.initialize();
        } else if (cfg) {
            this.lightning.setConfig(cfg);
        }
    }

    enableSun(cfg?: Partial<SunConfig>): void {
        if (!this.sun) {
            this.sun = new SunSystem('SunSystem', cfg);
            this.addChild(this.sun);
            this.sun.initialize();
        } else if (cfg) {
            this.sun.setConfig(cfg);
        }
    }
}
